/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { ContentCardViewProps } from '@airasia-phoenix/react-native/widgets/ContentCardView';
import { ContentCardViewStyle } from '@airasia-phoenix/react-native/widgets/ContentCardView';
import { ContentCardOrientation } from '@airasia-phoenix/react-native/widgets/HorizontalContentCardView/ContentDetailsView';
import { LabelType } from '@airasia-phoenix/react-native/components/LabelView/types';
import { pd, getTextStyle } from '@airasia-phoenix/react-native/utils/utils';
import { getCurrentBreakpoint } from '@airasia-rn/ui/utils';
import { Linking, Platform } from 'react-native';
import { createElement, type JSX } from 'react';
import DateRange from '@move-travel/react-native-phoenix-icons/DateRange';

const DEFAULT_CARD_SIZE = ContentCardViewStyle.REGULAR;
const WEB_CARD_IMAGE_HEIGHT = 236;
const EMPTY_IMAGE_URI = 'data:image/gif;base64,R0lGODlhAQABAAAAACw=';

const HOTEL_PRIMARY_TEXT_STYLE = getTextStyle(
  pd.sys.typescale.sub_body_text,
  pd.sys.colour.content_primary,
);

export type RecommendationItem = {
  itemId?: string;
  lob?: string;

  image?: string;
  title?: string;
  subTitle?: string;

  price?: string;
  originalPrice?: string;
  priceText?: string;
  priceClarification?: string;
  discount?: string;

  tags?: string[] | null;
  location?: string;
  icon?: string;

  rating?: number;
  reviewCount?: number;

  points?: string;

  deepLink?: string;

  // Campaign specific
  startDate?: string;
  endDate?: string;
  isEvergreen?: boolean;

  // Unformatted dates (epoch)
  unformatted?: {
    startEpocTime?: number;
    endEpocTime?: number;
  };
};

const formatDateRange = (
  startEpoch?: number,
  endEpoch?: number,
): string | null => {
  if (!startEpoch || !endEpoch) return null;

  const start = new Date(startEpoch * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  const end = new Date(endEpoch * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return `${start} - ${end}`;
};

const formatShortMonthDay = (date: Date) =>
  date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

const formatShortMonthDayYear = (date: Date) =>
  date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const navigateToDeepLink = (deepLink?: string, lob?: string) => {
  if (!deepLink) return;

  void Linking.openURL(deepLink).catch((error) => {
    console.error('[Recommendation] deep link open failed', {
      lob,
      deepLink,
      error,
    });
  });
};

const createCardPressHandler = (
  item: RecommendationItem,
  onCardPress?: (params: {
    itemId?: string;
    lob?: string;
    title?: string;
    deepLink?: string;
  }) => void,
) => {
  return () => {
    if (onCardPress) {
      onCardPress({
        itemId: item.itemId,
        lob: item.lob,
        title: item.title,
        deepLink: item.deepLink,
      });
      return;
    }
    navigateToDeepLink(item.deepLink, item.lob);
  };
};

const createCtaPressHandler = (
  item: RecommendationItem,
  onPressCta?: (params: { deepLink?: string }) => void,
) => {
  return () => {
    if (onPressCta) {
      onPressCta({
        deepLink: item.deepLink,
      });
      return;
    }
    navigateToDeepLink(item.deepLink, item.lob);
  };
};

/* Campaign Status Logic (CRITICAL BUSINESS RULE) */

const calculateCampaignStatus = (
  startDate?: string,
  endDate?: string,
  isEvergreen?: boolean,
) => {
  if (isEvergreen || !startDate || !endDate) return null;

  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  const startDiff = Math.floor((start.getTime() - now.getTime()) / 1000);
  const endDiff = Math.floor((end.getTime() - now.getTime()) / 1000);

  const DAY = 86400;
  const THREE_HOURS = 10800;
  const THREE_DAYS = 259200;

  // Not started
  if (startDiff > 0) {
    if (startDiff <= THREE_HOURS) {
      return {
        textType: 'timer',
        timerDate: start,
        placeholder: 'Starts in',
        showDateIcon: false,
      };
    }
    if (startDiff <= DAY) return { text: 'Starts today', showDateIcon: false };
    if (startDiff <= THREE_DAYS) {
      return {
        text: `Starts in ${Math.floor(startDiff / DAY)} days`,
        showDateIcon: false,
      };
    }
    return null;
  }

  // Active
  if (endDiff > 0 && endDiff <= DAY) {
    return {
      textType: 'timer',
      timerDate: end,
      placeholder: 'Ends in',
      showDateIcon: false,
    };
  }

  if (endDiff > DAY) {
    const startFormatted = formatShortMonthDay(start);
    const endFormatted = formatShortMonthDayYear(end);

    return { text: `${startFormatted} - ${endFormatted}`, showDateIcon: true };
  }

  return null;
};

const getSafeCurrentBreakpoint = () => {
  try {
    return getCurrentBreakpoint();
  } catch {
    return 2;
  }
};

const getBaseCard = (item: RecommendationItem, cardSize?: number) => {
  const imageUri = item.image?.trim();
  const currentBreakpoint = getSafeCurrentBreakpoint();
  const isMobileWidth = currentBreakpoint < 4;
  const isDesktopWeb = Platform.OS === 'web' && !isMobileWidth;
  const imageProps = imageUri ? { url: imageUri } : { uri: EMPTY_IMAGE_URI };

  if (isDesktopWeb) {
    return {
      image: imageProps,
      imageWidth: '100%',
      imageHeight: WEB_CARD_IMAGE_HEIGHT,
      contentDetailsPropsValues: {
        width: DEFAULT_CARD_SIZE,
        imageWidth: DEFAULT_CARD_SIZE,
      },
    } as unknown as Partial<ContentCardViewProps>;
  }

  const imageSize = cardSize ?? DEFAULT_CARD_SIZE;

  return {
    image: imageProps,
    imageWidth: imageSize,
    imageHeight: imageSize,
    contentDetailsPropsValues: {
      width: imageSize,
      imageWidth: imageSize,
    },
  } as unknown as Partial<ContentCardViewProps>;
};

const mapHotel = (
  item: RecommendationItem,
  onCardPress?: (params: {
    itemId?: string;
    lob?: string;
    title?: string;
    deepLink?: string;
  }) => void,
  cardSize?: number,
  onPressCta?: (params: { deepLink?: string }) => void,
): ContentCardViewProps => {
  const base = getBaseCard(item, cardSize);

  return {
    ...base,
    supportingInfo: item.location
      ? {
          supportingIcon: {
            icon: pd.icon.main.communication.get('location_on'),
          },
          supportingText: item.location,
        }
      : undefined,

    contentDetailsPropsValues: {
      ...base.contentDetailsPropsValues,
      orientation: ContentCardOrientation.VERTICAL,
      primaryText: item.title,
      primaryTextStyle: HOTEL_PRIMARY_TEXT_STYLE,
      secondaryText: formatDateRange(
        item.unformatted?.startEpocTime,
        item.unformatted?.endEpocTime,
      ),
      subLabel: {
        type: LabelType.TONED_HIGH,
        text: item.tags?.join(' ') ?? 'Hotel',
      },
      valueStarting: item.priceClarification,
      value: item.price,
      valueSlash: item.originalPrice,
      valueEnding: item.priceText ?? 'per night',
    },
    onPress: createCardPressHandler(item, onCardPress),
  } as ContentCardViewProps;
};

const mapFlight = (
  item: RecommendationItem,
  onCardPress?: (params: {
    itemId?: string;
    lob?: string;
    title?: string;
    deepLink?: string;
  }) => void,
  cardSize?: number,
  onPressCta?: (params: { deepLink?: string }) => void,
): ContentCardViewProps => {
  const base = getBaseCard(item, cardSize);

  return {
    ...base,

    contentDetailsPropsValues: {
      ...base.contentDetailsPropsValues,
      orientation: ContentCardOrientation.VERTICAL,
      primaryText: item.title,
      secondaryText: formatDateRange(
        item.unformatted?.startEpocTime,
        item.unformatted?.endEpocTime,
      ),
      subLabel: {
        type: LabelType.TONED_HIGH,
        text: 'Flight',
      },
      valueStarting: item.priceClarification,
      value: item.price,
    },

    onPress: createCardPressHandler(item, onCardPress),
  } as ContentCardViewProps;
};

const mapAncillary = (
  item: RecommendationItem,
  onCardPress?: (params: {
    itemId?: string;
    lob?: string;
    title?: string;
    deepLink?: string;
  }) => void,
  cardSize?: number,
  onPressCta?: (params: { deepLink?: string }) => void,
): ContentCardViewProps => {
  const base = getBaseCard(item, cardSize);

  return {
    ...base,
    contentDetailsPropsValues: {
      ...base.contentDetailsPropsValues,
      orientation: ContentCardOrientation.VERTICAL,
      primaryText: item.title,
      valueStarting: item.priceClarification,
      value: item.price,
      containerStyle: { marginTop: 10 },
    },

    onPress: createCardPressHandler(item, onCardPress),
  } as ContentCardViewProps;
};

const mapPromotions = (
  item: RecommendationItem,
  onCardPress?: (params: {
    itemId?: string;
    lob?: string;
    title?: string;
    deepLink?: string;
  }) => void,
  cardSize?: number,
  onPressCta?: (params: { deepLink?: string }) => void,
): ContentCardViewProps | null => {
  const base = getBaseCard(item, cardSize);
  const now = Date.now();
  const startTime = item.startDate ? new Date(item.startDate).getTime() : null;
  const endTime = item.endDate ? new Date(item.endDate).getTime() : null;
  const THREE_DAYS_IN_MS = 3 * 24 * 60 * 60 * 1000;

  // Hide if start date is more than 3 days ahead.
  if (startTime && startTime > now + THREE_DAYS_IN_MS) {
    return null;
  }

  // Hide if campaign already ended.
  if (endTime && endTime <= now) {
    return null;
  }

  const campaignStatus = calculateCampaignStatus(
    item.startDate,
    item.endDate,
    item.isEvergreen,
  );

  let supportingText;
  let supportingIcon;

  if (!item.isEvergreen && campaignStatus) {
    if (campaignStatus.textType === 'timer') {
      supportingText = campaignStatus.placeholder;
      supportingIcon = undefined;

      // supportingText2 = formatPromotionDateRange(item.startDate, item.endDate);
    } else if (campaignStatus.text) {
      supportingText = campaignStatus.text;
      supportingIcon = campaignStatus.showDateIcon
        ? {
            children: createElement(DateRange) as JSX.Element,
          }
        : undefined;

      // supportingText2 = formatPromotionDateRange(item.startDate, item.endDate);
    }
  }

  return {
    ...base,

    contentDetailsPropsValues: {
      ...base.contentDetailsPropsValues,

      orientation: ContentCardOrientation.VERTICAL,

      primaryText: item.title,

      secondaryText: item.subTitle,

      mainLabel: item.tags?.length
        ? {
            text: item.tags.join(' '),

            type: LabelType.FILLED,
          }
        : undefined,
    },

    supportingInfo: campaignStatus
      ? {
          supportingText,
          supportingIcon,
        }
      : undefined,

    __timer:
      campaignStatus?.textType === 'timer'
        ? {
            timerDate: campaignStatus.timerDate,

            prefix: campaignStatus.placeholder,
          }
        : undefined,

    onPress: createCardPressHandler(item, onCardPress),
  } as ContentCardViewProps;
};

const mapCommunity = (
  item: RecommendationItem,
  onCardPress?: (params: {
    itemId?: string;
    lob?: string;
    title?: string;
    deepLink?: string;
  }) => void,
  cardSize?: number,
  onPressCta?: (params: { deepLink?: string }) => void,
): ContentCardViewProps => {
  const base = getBaseCard(item, cardSize);

  return {
    ...base,
    contentDetailsPropsValues: {
      ...base.contentDetailsPropsValues,
      primaryText: item.title,
      orientation: ContentCardOrientation.VERTICAL,
      subLabel: {
        type: LabelType.TONED_HIGH,
        text: 'Community',
      },
      tertiaryInfo: {
        iconAndText: {
          icon: {
            icon: pd.icon.main.social.get('group'),
          },
          text: item.subTitle ?? '',
        },
      },
    },
    ctaText: 'Join the chatroom',
    onPressCta: createCtaPressHandler(item, onPressCta),
    onPress: createCardPressHandler(item, onCardPress),
  } as ContentCardViewProps;
};

const mapRewards = (
  item: RecommendationItem,
  onCardPress?: (params: {
    itemId?: string;
    lob?: string;
    title?: string;
    deepLink?: string;
  }) => void,
  cardSize?: number,
  onPressCta?: (params: { deepLink?: string }) => void,
): ContentCardViewProps => {
  const base = getBaseCard(item, cardSize);

  return {
    ...base,
    contentDetailsPropsValues: {
      ...base.contentDetailsPropsValues,
      orientation: ContentCardOrientation.VERTICAL,
      primaryText: item.title,
      secondaryText: item.subTitle,
      subLabel: {
        type: LabelType.TONED_HIGH,
        text: 'Rewards',
      },
    },
    onPress: createCardPressHandler(item, onCardPress),
  } as ContentCardViewProps;
};

const mapAd = (
  item: RecommendationItem,
  onCardPress?: (params: {
    itemId?: string;
    lob?: string;
    title?: string;
    deepLink?: string;
  }) => void,
  cardSize?: number,
  onPressCta?: (params: { deepLink?: string }) => void,
): ContentCardViewProps => {
  const base = getBaseCard(item, cardSize);

  return {
    ...base,

    contentDetailsPropsValues: {
      ...base.contentDetailsPropsValues,

      orientation: ContentCardOrientation.VERTICAL,

      primaryText: item.title ?? 'Sponsored',

      secondaryText: item.subTitle,
    },

    onPress: createCardPressHandler(item, onCardPress),
  } as ContentCardViewProps;
};

/* Public Mapper */

const LOB_MAPPERS: Record<
  string,
  (
    item: RecommendationItem,
    onCardPress?: (params: {
      itemId?: string;
      lob?: string;
      title?: string;
      deepLink?: string;
    }) => void,
    cardSize?: number,
    onPressCta?: (params: { deepLink?: string }) => void,
  ) => ContentCardViewProps | null
> = {
  hotel: mapHotel,
  flight: mapFlight,
  ancillary: mapAncillary,
  promotions: mapPromotions,
  community: mapCommunity,
  rewards: mapRewards,
  ad_v3: mapAd,
};

export const mapRecommendationToCardProps = (
  item?: RecommendationItem | null,
  onCardPress?: (params: {
    itemId?: string;
    lob?: string;
    title?: string;
    deepLink?: string;
  }) => void,
  cardSize?: number,
  onPressCta?: (params: { deepLink?: string }) => void,
): ContentCardViewProps | null => {
  if (!item?.lob) return null;
  return (
    LOB_MAPPERS[item.lob]?.(item, onCardPress, cardSize, onPressCta) ?? null
  );
};

export default mapRecommendationToCardProps;
